import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Search, Filter, ChevronDown, ChevronRight, Tv, Package, IndianRupee, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Channels = () => {
  const { broadcasterId } = useParams();
  const [broadcasters, setBroadcasters] = useState([]);
  const [selectedBroadcaster, setSelectedBroadcaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');

  useEffect(() => {
    const fetchBroadcasters = async () => {
      try {
        const response = await axios.get(`${API}/broadcasters`);
        setBroadcasters(response.data);
        
        if (broadcasterId) {
          const broadcaster = response.data.find(b => b.id === broadcasterId);
          setSelectedBroadcaster(broadcaster || null);
        }
      } catch (error) {
        console.error('Error fetching broadcasters:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBroadcasters();
  }, [broadcasterId]);

  const allCategories = [...new Set(broadcasters.flatMap(b => b.channels.map(c => c.category)))];
  const allLanguages = [...new Set(broadcasters.flatMap(b => b.channels.map(c => c.language)))];

  const filterChannels = (channels) => {
    return channels.filter(channel => {
      const matchesSearch = channel.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || channel.category === categoryFilter;
      const matchesLanguage = languageFilter === 'all' || channel.language === languageFilter;
      return matchesSearch && matchesCategory && matchesLanguage;
    });
  };

  const getQualityBadge = (quality) => {
    if (quality.includes('HD')) {
      return <span className="badge badge-hd">HD</span>;
    }
    return <span className="badge badge-default">SD</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="channels-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D92323] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading channels...</p>
        </div>
      </div>
    );
  }

  // Single Broadcaster View
  if (selectedBroadcaster) {
    const filteredChannels = filterChannels(selectedBroadcaster.channels);
    
    return (
      <div data-testid="broadcaster-detail-page">
        {/* Header */}
        <section className="bg-white border-b border-gray-100 py-8">
          <div className="container-custom">
            <Link 
              to="/channels" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-[#D92323] mb-6 transition-colors"
              data-testid="back-to-channels"
            >
              <ArrowLeft size={18} strokeWidth={2} />
              Back to All Broadcasters
            </Link>
            
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl"
                style={{ backgroundColor: selectedBroadcaster.logo_color }}
              >
                {selectedBroadcaster.name.charAt(0)}
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {selectedBroadcaster.name}
                </h1>
                <p className="text-gray-600 mt-1">{selectedBroadcaster.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="section bg-[#F9FAFB]">
          <div className="container-custom">
            <Tabs defaultValue="channels" className="w-full">
              <TabsList className="mb-8 bg-white border border-gray-200">
                <TabsTrigger value="channels" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white" data-testid="tab-channels">
                  <Tv size={16} className="mr-2" />
                  Channels ({selectedBroadcaster.channels.length})
                </TabsTrigger>
                <TabsTrigger value="bouquets" className="data-[state=active]:bg-[#D92323] data-[state=active]:text-white" data-testid="tab-bouquets">
                  <Package size={16} className="mr-2" />
                  Bouquets ({selectedBroadcaster.bouquets.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="channels">
                {/* Search and Filters */}
                <div className="bg-white rounded-2xl p-6 mb-6 border border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search channels..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        data-testid="search-channels"
                      />
                    </div>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="h-12" data-testid="category-filter">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {allCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={languageFilter} onValueChange={setLanguageFilter}>
                      <SelectTrigger className="h-12" data-testid="language-filter">
                        <SelectValue placeholder="All Languages" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Languages</SelectItem>
                        {allLanguages.map(lang => (
                          <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Channels Grid */}
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="text-left px-6 py-4 font-semibold text-gray-700">Channel</th>
                          <th className="text-left px-6 py-4 font-semibold text-gray-700">Category</th>
                          <th className="text-left px-6 py-4 font-semibold text-gray-700">Language</th>
                          <th className="text-left px-6 py-4 font-semibold text-gray-700">Quality</th>
                          <th className="text-right px-6 py-4 font-semibold text-gray-700">MRP/Month</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredChannels.map((channel, index) => (
                          <tr 
                            key={index} 
                            className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                            data-testid={`channel-row-${index}`}
                          >
                            <td className="px-6 py-4">
                              <span className="font-semibold text-gray-900">{channel.name}</span>
                            </td>
                            <td className="px-6 py-4 text-gray-600">{channel.category}</td>
                            <td className="px-6 py-4 text-gray-600">{channel.language}</td>
                            <td className="px-6 py-4">{getQualityBadge(channel.quality)}</td>
                            <td className="px-6 py-4 text-right">
                              <span className="font-bold text-[#D92323] font-mono-condensed text-lg">
                                ₹{channel.price}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {filteredChannels.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No channels found matching your filters
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="bouquets">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {selectedBroadcaster.bouquets.map((bouquet, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:border-[#D92323]/20 transition-all"
                      data-testid={`bouquet-card-${index}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span className={`badge ${bouquet.type === 'HD' ? 'badge-hd' : 'badge-default'} mb-2`}>
                            {bouquet.type}
                          </span>
                          <h3 className="font-bold text-gray-900">{bouquet.name}</h3>
                        </div>
                        <div className="text-right">
                          <span className="text-3xl font-bold text-[#D92323] font-mono-condensed">
                            ₹{bouquet.price}
                          </span>
                          <p className="text-xs text-gray-500">/month</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Tv size={16} strokeWidth={2} />
                        <span>{bouquet.channels_count} Channels included</span>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </div>
    );
  }

  // All Broadcasters View
  return (
    <div data-testid="channels-page">
      {/* Hero */}
      <section className="bg-white py-12 md:py-16 border-b border-gray-100">
        <div className="container-custom">
          <span className="badge badge-featured mb-4">RIO Agreements</span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Channel Packages & Pricing
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Browse channels from 8+ premium broadcasters. All prices are TRAI compliant MRP 
            and same across all states and union territories.
          </p>
        </div>
      </section>

      {/* Broadcaster List */}
      <section className="section bg-[#F9FAFB]">
        <div className="container-custom">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center">
              <div className="text-3xl font-bold text-[#D92323] font-mono-condensed">
                {broadcasters.length}
              </div>
              <div className="text-sm text-gray-500 mt-1">Broadcasters</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center">
              <div className="text-3xl font-bold text-[#D92323] font-mono-condensed">
                {broadcasters.reduce((acc, b) => acc + b.channels.length, 0)}
              </div>
              <div className="text-sm text-gray-500 mt-1">Channels</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center">
              <div className="text-3xl font-bold text-[#D92323] font-mono-condensed">
                {broadcasters.reduce((acc, b) => acc + b.bouquets.length, 0)}
              </div>
              <div className="text-sm text-gray-500 mt-1">Bouquets</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center">
              <div className="text-3xl font-bold text-[#D92323] font-mono-condensed">
                All India
              </div>
              <div className="text-sm text-gray-500 mt-1">Coverage</div>
            </div>
          </div>

          {/* Broadcasters Accordion */}
          <Accordion type="single" collapsible className="space-y-4" data-testid="broadcasters-accordion">
            {broadcasters.map((broadcaster) => (
              <AccordionItem 
                key={broadcaster.id} 
                value={broadcaster.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
              >
                <AccordionTrigger 
                  className="px-6 py-4 hover:no-underline hover:bg-gray-50 transition-colors [&[data-state=open]]:bg-gray-50"
                  data-testid={`accordion-${broadcaster.id}`}
                >
                  <div className="flex items-center gap-4 w-full">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                      style={{ backgroundColor: broadcaster.logo_color }}
                    >
                      {broadcaster.name.charAt(0)}
                    </div>
                    <div className="text-left flex-1">
                      <h3 className="font-bold text-gray-900">{broadcaster.name}</h3>
                      <p className="text-sm text-gray-500 line-clamp-1">{broadcaster.description}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-6 text-sm text-gray-500 mr-4">
                      <span className="flex items-center gap-1">
                        <Tv size={16} />
                        {broadcaster.channels.length} Channels
                      </span>
                      <span className="flex items-center gap-1">
                        <Package size={16} />
                        {broadcaster.bouquets.length} Bouquets
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  {/* Quick Channel Preview */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                    {broadcaster.channels.slice(0, 8).map((channel, index) => (
                      <div 
                        key={index}
                        className="bg-gray-50 rounded-xl p-3 flex items-center justify-between"
                      >
                        <span className="text-sm font-medium text-gray-700 truncate mr-2">
                          {channel.name}
                        </span>
                        <span className="text-sm font-bold text-[#D92323] font-mono-condensed flex-shrink-0">
                          ₹{channel.price}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Bouquets Preview */}
                  {broadcaster.bouquets.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3">Popular Bouquets</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {broadcaster.bouquets.slice(0, 3).map((bouquet, index) => (
                          <div 
                            key={index}
                            className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className={`badge ${bouquet.type === 'HD' ? 'badge-hd' : 'badge-default'} mb-1`}>
                                  {bouquet.type}
                                </span>
                                <h5 className="font-semibold text-gray-900 text-sm">{bouquet.name}</h5>
                                <p className="text-xs text-gray-500">{bouquet.channels_count} channels</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xl font-bold text-[#D92323] font-mono-condensed">
                                  ₹{bouquet.price}
                                </span>
                                <p className="text-xs text-gray-500">/month</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View All Button */}
                  <Link
                    to={`/channels/${broadcaster.id}`}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid={`view-all-${broadcaster.id}`}
                  >
                    View All {broadcaster.channels.length} Channels
                    <ChevronRight size={18} strokeWidth={2} />
                  </Link>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Info Section */}
      <section className="section bg-white" data-testid="rio-info">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              About RIO Agreements
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Reference Interconnect Offer (RIO) is a document mandated by TRAI that lists 
              the maximum retail prices for individual channels and bouquets offered by broadcasters. 
              All prices shown are MRP per month and are same across all states and union territories.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-1">A-la-carte</h4>
                <p className="text-sm text-gray-600">Choose individual channels as per your preference</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Bouquets</h4>
                <p className="text-sm text-gray-600">Pre-defined channel bundles at discounted rates</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-1">HD Channels</h4>
                <p className="text-sm text-gray-600">Premium quality channels with higher resolution</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#0A0A0A] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Help Choosing?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Our team can help you build the perfect channel package for your family. Contact us today!
          </p>
          <a href="tel:9019206699" className="btn-primary inline-flex items-center gap-2" data-testid="channels-cta">
            Call 9019206699
          </a>
        </div>
      </section>
    </div>
  );
};

export default Channels;
